import api from './authService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const vendorWorkspaceApi = {
  plan: async () => {
    const response = await api.get('/vendor/workspace/plan');
    return response.data;
  },
  today: async (params = {}) => {
    const response = await api.get('/vendor/workspace/today', { params });
    return response.data;
  },
  insights: async (params = {}) => {
    const response = await api.get('/vendor/workspace/insights', { params });
    return response.data;
  },
  linkOptions: async () => {
    const response = await api.get('/vendor/workspace/link-options');
    return response.data;
  },
  exportCsv: async (params = {}) => {
    const response = await api.get('/vendor/workspace/export.csv', {
      params,
      responseType: 'blob',
    });
    return response;
  },
  automations: async () => {
    const response = await api.get('/vendor/workspace/automations');
    return response.data;
  },
  toggleAutomation: async ({ recipeKey, enabled }) => {
    const response = await api.put(`/vendor/workspace/automations/${recipeKey}`, {
      enabled,
    });
    return response.data;
  },
  createCustomWorkflow: async (payload) => {
    const response = await api.post('/vendor/workspace/automations/custom', payload);
    return response.data;
  },
  updateCustomWorkflow: async ({ id, ...payload }) => {
    const response = await api.put(`/vendor/workspace/automations/custom/${id}`, payload);
    return response.data;
  },
  deleteCustomWorkflow: async (id) => {
    const response = await api.delete(`/vendor/workspace/automations/custom/${id}`);
    return response.data;
  },
  automationRuns: async (params = {}) => {
    const response = await api.get('/vendor/workspace/automations/runs', { params });
    return response.data;
  },
  agents: async () => {
    const response = await api.get('/vendor/workspace/agents');
    return response.data;
  },
  runAgent: async (payload) => {
    const response = await api.post('/vendor/workspace/agents/run', payload);
    return response.data;
  },
  agentRuns: async (params = {}) => {
    const response = await api.get('/vendor/workspace/agents/runs', { params });
    return response.data;
  },
  list: async (params = {}) => {
    const response = await api.get('/vendor/workspace/items', { params });
    return response.data;
  },
  create: async (payload) => {
    const response = await api.post('/vendor/workspace/items', payload);
    return response.data;
  },
  update: async ({ id, data }) => {
    const response = await api.put(`/vendor/workspace/items/${id}`, data);
    return response.data;
  },
  complete: async (id) => {
    const response = await api.post(`/vendor/workspace/items/${id}/complete`);
    return response.data;
  },
  move: async ({ id, status, sort_order, ordered_ids }) => {
    const response = await api.post(`/vendor/workspace/items/${id}/move`, {
      status,
      sort_order,
      ordered_ids,
    });
    return response.data;
  },
  whatsappDraft: async ({ id, template, phone }) => {
    const response = await api.post(`/vendor/workspace/items/${id}/whatsapp-draft`, {
      template,
      phone,
    });
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/vendor/workspace/items/${id}`);
    return response.data;
  },
};

const invalidateWorkspace = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['vendorWorkspace'] });
};

export const useVendorWorkspaceToday = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'today', params],
    queryFn: () => vendorWorkspaceApi.today(params),
    staleTime: 15_000,
    refetchInterval: 45_000,
    ...options,
  });

export const useVendorWorkspaceInsights = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'insights', params],
    queryFn: () => vendorWorkspaceApi.insights(params),
    staleTime: 30_000,
    ...options,
  });

export const useVendorWorkspaceLinkOptions = (options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'linkOptions'],
    queryFn: () => vendorWorkspaceApi.linkOptions(),
    staleTime: 60_000,
    retry: false,
    ...options,
  });

export const useVendorWorkspaceAutomations = (options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'automations'],
    queryFn: () => vendorWorkspaceApi.automations(),
    staleTime: 20_000,
    ...options,
  });

export const useVendorWorkspaceAutomationRuns = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'automationRuns', params],
    queryFn: () => vendorWorkspaceApi.automationRuns(params),
    staleTime: 15_000,
    ...options,
  });

export const useToggleWorkspaceAutomation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.toggleAutomation,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'automations'] });
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'automationRuns'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useCreateCustomWorkflow = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.createCustomWorkflow,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'automations'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useUpdateCustomWorkflow = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.updateCustomWorkflow,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'automations'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useDeleteCustomWorkflow = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.deleteCustomWorkflow,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'automations'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useVendorWorkspaceAgents = (options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'agents'],
    queryFn: () => vendorWorkspaceApi.agents(),
    staleTime: 60_000,
    ...options,
  });

export const useVendorWorkspaceAgentRuns = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'agentRuns', params],
    queryFn: () => vendorWorkspaceApi.agentRuns(params),
    staleTime: 15_000,
    ...options,
  });

export const useRunWorkspaceAgent = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.runAgent,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'agentRuns'] });
      queryClient.invalidateQueries({ queryKey: ['vendorWorkspace', 'insights'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useVendorWorkspaceItems = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorWorkspace', 'items', params],
    queryFn: () => vendorWorkspaceApi.list(params),
    staleTime: 15_000,
    ...options,
  });

export const useCreateWorkspaceItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.create,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useUpdateWorkspaceItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.update,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useCompleteWorkspaceItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.complete,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useMoveWorkspaceItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.move,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useWorkspaceWhatsappDraft = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.whatsappDraft,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useDeleteWorkspaceItem = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: vendorWorkspaceApi.remove,
    onSuccess: (...args) => {
      invalidateWorkspace(queryClient);
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};
